import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QueryClient } from "@tanstack/react-query";

interface ErrorDisplayProps {
  title?: string;
  description?: string;
  action?: () => void;
  actionLabel?: string;
  queryClient?: QueryClient;
}

export function ErrorDisplay({
  title = "New Version Available",
  description = "Please refresh to get the latest experience.",
  action,
  actionLabel = "Refresh",
  queryClient
}: ErrorDisplayProps) {
  const handleRetry = () => {
    if (action) {
      action();
    } else if (queryClient) {
      queryClient.invalidateQueries();
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center p-8 text-center max-w-sm mx-auto">
      {/* Neon glow effect */}
      <div 
        className="absolute inset-0 rounded-3xl blur-2xl opacity-60"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.4) 0%, rgba(236, 72, 153, 0.2) 50%, transparent 70%)',
        }}
      />
      
      {/* Glass card */}
      <div 
        className="relative z-10 flex flex-col items-center p-8 rounded-3xl border border-white/10"
        style={{
          background: 'rgba(20, 20, 24, 0.8)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          boxShadow: '0 0 60px rgba(139, 92, 246, 0.15), 0 0 100px rgba(236, 72, 153, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* BuxBox Logo */}
        <div className="relative mb-6">
          <div 
            className="absolute inset-0 blur-xl opacity-80"
            style={{
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.6) 0%, transparent 70%)',
            }}
          />
          <img 
            src="/logo.png" 
            alt="BuxBox" 
            className="relative w-16 h-16 object-contain"
          />
        </div>
        
        <h3 
          className="text-xl font-semibold text-white mb-2 tracking-tight"
          style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}
        >
          {title}
        </h3>
        
        <p className="text-white/60 mb-6 text-sm leading-relaxed">
          {description}
        </p>
        
        <Button 
          onClick={handleRetry}
          className="gap-2 px-6 h-11 text-white font-medium rounded-xl transition-all duration-300"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.8) 0%, rgba(236, 72, 153, 0.8) 100%)',
            boxShadow: '0 0 20px rgba(139, 92, 246, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
          }}
        >
          <RefreshCw className="h-4 w-4" />
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}
